import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Grid, 
  Button, 
  Checkbox,
  FormControlLabel,
  FormGroup,
  Card,
  CardContent,
  CardActions,
  Divider,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Stepper,
  Step,
  StepLabel
} from '@mui/material';
import {
  Launch as LaunchIcon,
  Share as ShareIcon,
  CloudUpload as CloudUploadIcon,
  FileCopy as FileCopyIcon,
  Facebook as FacebookIcon,
  Twitter as TwitterIcon,
  Instagram as InstagramIcon,
  Message as MessageIcon,
  Email as EmailIcon,
  CheckCircle as CheckCircleIcon,
  ContentCopy as CopyIcon,
  Code as CodeIcon,
  MoreVert as MoreVertIcon
} from '@mui/icons-material';
import { AdStrategyResponse, AdVariant, AdFormat } from '../../services/adStrategistService';

interface DistributionTabProps {
  strategy: AdStrategyResponse | null;
}

const DistributionTab: React.FC<DistributionTabProps> = ({ strategy }) => {
  const [selectedPlatforms, setSelectedPlatforms] = useState<Record<string, boolean>>({
    facebook: true,
    instagram: true,
    twitter: false,
    google: false,
    linkedin: false,
    email: false,
    chatbot: false
  });
  
  const [isExporting, setIsExporting] = useState(false);
  const [exportSuccess, setExportSuccess] = useState(false);
  const [showConnectDialog, setShowConnectDialog] = useState(false);
  const [copiedStatus, setCopiedStatus] = useState<Record<string, boolean>>({});
  const [activeStep, setActiveStep] = useState(0);
  const [publishSuccess, setPublishSuccess] = useState<string | null>(null);
  
  if (!strategy) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column', p: 5 }}>
        <CircularProgress sx={{ mb: 2 }} />
        <Typography variant="h6">Loading strategy...</Typography>
      </Box>
    );
  }
  
  const handlePlatformChange = (platform: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedPlatforms({
      ...selectedPlatforms,
      [platform]: event.target.checked
    });
  };
  
  const handleExportClick = () => {
    setIsExporting(true);
    
    // Simulate export process
    setTimeout(() => {
      setIsExporting(false);
      setExportSuccess(true);
      
      // Reset success message after a few seconds
      setTimeout(() => {
        setExportSuccess(false);
      }, 3000);
    }, 2000);
  };
  
  const handleConnectPlatform = () => {
    setShowConnectDialog(true);
  };
  
  const handleConnectDialogClose = () => {
    setShowConnectDialog(false);
  };
  
  const handleCopyCode = (variant: string) => {
    const variantsByFormat = strategy.ad_variants.reduce<Record<string, AdVariant[]>>((acc, variant) => {
      const format = variant.format;
      if (!acc[format]) acc[format] = [];
      acc[format].push(variant);
      return acc;
    }, {});
    
    const selectedVariant = variantsByFormat[variant]?.[0];
    
    if (selectedVariant) {
      let code = '';
      
      if (variant === AdFormat.CHATBOT_SCRIPT) {
        code = `// Reasonic Exclusive Chatbot Script
const reasonicChatbot = {
  id: "${strategy.id}-${Date.now()}",
  script: \`${selectedVariant.content}\`,
  triggers: ["product", "pricing", "help"],
  onLoad: function() { console.log("Reasonic chatbot loaded"); }
};

// Insert at the bottom of your page
document.addEventListener("DOMContentLoaded", function() {
  const script = document.createElement("script");
  script.src = "https://cdn.reasonic.com/chatbot-loader.js";
  script.setAttribute("data-reasonic-id", reasonicChatbot.id);
  document.body.appendChild(script);
});`;
      } else if (variant === AdFormat.EMAIL_NEWSLETTER) {
        code = `<!-- Reasonic Newsletter Component -->
<div class="reasonic-newsletter" data-campaign-id="${strategy.id}">
  <h2>${selectedVariant.title || "Newsletter Signup"}</h2>
  <p>${selectedVariant.content}</p>
  <div class="reasonic-cta">
    <a href="#" class="reasonic-button">${selectedVariant.call_to_action || "Subscribe Now"}</a>
  </div>
  <script src="https://cdn.reasonic.com/newsletter-tracker.js" async></script>
</div>`;
      }
      
      navigator.clipboard.writeText(code).then(() => {
        setCopiedStatus({ ...copiedStatus, [variant]: true });
        setTimeout(() => {
          setCopiedStatus({ ...copiedStatus, [variant]: false });
        }, 2000);
      });
    }
  };
  
  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleReset = () => {
    setActiveStep(0);
  };
  
  const steps = ['Select Platforms', 'Configure Settings', 'Publish Ads'];
  
  const formatName = (format: string): string => {
    return format.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };
  
  // Group variants by format
  const variantsByFormat = strategy.ad_variants.reduce<Record<string, AdVariant[]>>((acc, variant) => {
    const format = variant.format;
    if (!acc[format]) acc[format] = [];
    acc[format].push(variant);
    return acc;
  }, {});
  
  const platformIcons: Record<string, React.ReactNode> = {
    facebook: <FacebookIcon color="primary" />,
    instagram: <InstagramIcon color="secondary" />,
    twitter: <TwitterIcon color="info" />,
    google: <img src="/google-ads-icon.png" alt="Google" style={{ width: 24, height: 24 }} />,
    linkedin: <img src="/linkedin-icon.png" alt="LinkedIn" style={{ width: 24, height: 24 }} />,
    email: <EmailIcon color="action" />,
    chatbot: <MessageIcon color="success" />
  };
  
  const exclusiveFormats = [AdFormat.CHATBOT_SCRIPT, AdFormat.EMAIL_NEWSLETTER];
  
  const handlePublish = (platform: string) => {
    setIsExporting(true);
    
    // Simulate publishing process
    setTimeout(() => {
      setIsExporting(false);
      
      // Set platform-specific success message
      const platformName = platform.charAt(0).toUpperCase() + platform.slice(1);
      setPublishSuccess(`Successfully published to ${platformName} Ads!`);
      
      // Reset success message after a few seconds
      setTimeout(() => {
        setPublishSuccess(null);
      }, 3000);
    }, 1000);
  };
  
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">Distribution</Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={isExporting ? <CircularProgress size={20} color="inherit" /> : <ShareIcon />}
          onClick={handleExportClick}
          disabled={isExporting}
        >
          {isExporting ? 'Exporting...' : 'Export All Selected'}
        </Button>
      </Box>
      
      {exportSuccess && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Successfully exported ad variants to selected platforms!
        </Alert>
      )}
      
      {publishSuccess && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {publishSuccess}
        </Alert>
      )}
      
      <Paper elevation={1} sx={{ p: 3, mb: 4 }}>
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        
        {activeStep === 0 && (
          <Box>
            <Typography variant="h6" gutterBottom>Select Distribution Platforms</Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Choose where you want to publish your ad variants. Standard ad formats can be distributed 
              to multiple platforms, while exclusive formats are only available through Reasonic partners.
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" gutterBottom>Standard Platforms</Typography>
                <FormGroup>
                  <FormControlLabel
                    control={<Checkbox checked={selectedPlatforms.facebook} onChange={handlePlatformChange('facebook')} />}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <FacebookIcon color="primary" sx={{ mr: 1 }} />
                        Facebook Ads
                      </Box>
                    }
                  />
                  <FormControlLabel
                    control={<Checkbox checked={selectedPlatforms.instagram} onChange={handlePlatformChange('instagram')} />}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <InstagramIcon color="secondary" sx={{ mr: 1 }} />
                        Instagram Ads
                      </Box>
                    }
                  />
                  <FormControlLabel
                    control={<Checkbox checked={selectedPlatforms.twitter} onChange={handlePlatformChange('twitter')} />}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <TwitterIcon color="info" sx={{ mr: 1 }} />
                        Twitter Ads
                      </Box>
                    }
                  />
                  <FormControlLabel
                    control={<Checkbox checked={selectedPlatforms.google} onChange={handlePlatformChange('google')} />}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <img src="/google-ads-icon.png" alt="Google" style={{ width: 24, height: 24, marginRight: 8 }} />
                        Google Ads
                      </Box>
                    }
                  />
                  <FormControlLabel
                    control={<Checkbox checked={selectedPlatforms.linkedin} onChange={handlePlatformChange('linkedin')} />}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <img src="/linkedin-icon.png" alt="LinkedIn" style={{ width: 24, height: 24, marginRight: 8 }} />
                        LinkedIn Ads
                      </Box>
                    }
                  />
                </FormGroup>
                
                <Button 
                  variant="outlined" 
                  color="primary" 
                  sx={{ mt: 2 }}
                  onClick={handleConnectPlatform}
                >
                  Connect Platform
                </Button>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" gutterBottom>Exclusive Formats</Typography>
                <FormGroup>
                  <FormControlLabel
                    control={<Checkbox checked={selectedPlatforms.chatbot} onChange={handlePlatformChange('chatbot')} />}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <MessageIcon color="success" sx={{ mr: 1 }} />
                        Reasonic Chatbot
                      </Box>
                    }
                  />
                  <FormControlLabel
                    control={<Checkbox checked={selectedPlatforms.email} onChange={handlePlatformChange('email')} />}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <EmailIcon color="action" sx={{ mr: 1 }} />
                        Reasonic Email Newsletters
                      </Box>
                    }
                  />
                </FormGroup>
                
                <Button 
                  variant="outlined" 
                  color="secondary" 
                  sx={{ mt: 2 }}
                  component="a"
                  href="https://publishers.reasonic.com"
                  target="_blank"
                  startIcon={<LaunchIcon />}
                >
                  Reasonic Publisher Network
                </Button>
              </Grid>
            </Grid>
            
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4 }}>
              <Button
                variant="contained"
                onClick={handleNext}
              >
                Continue
              </Button>
            </Box>
          </Box>
        )}
        
        {activeStep === 1 && (
          <Box>
            <Typography variant="h6" gutterBottom>Configure Distribution Settings</Typography>
            
            <Grid container spacing={3}>
              {selectedPlatforms.facebook && (
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <FacebookIcon color="primary" sx={{ mr: 1 }} />
                        <Typography variant="h6">Facebook Ads</Typography>
                      </Box>
                      
                      <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>Campaign Objective</InputLabel>
                        <Select
                          defaultValue="conversion"
                          label="Campaign Objective"
                        >
                          <MenuItem value="awareness">Brand Awareness</MenuItem>
                          <MenuItem value="consideration">Consideration</MenuItem>
                          <MenuItem value="conversion">Conversion</MenuItem>
                        </Select>
                      </FormControl>
                      
                      <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>Ad Format</InputLabel>
                        <Select
                          defaultValue="feed"
                          label="Ad Format"
                        >
                          <MenuItem value="feed">Feed Ad</MenuItem>
                          <MenuItem value="story">Story Ad</MenuItem>
                          <MenuItem value="carousel">Carousel Ad</MenuItem>
                        </Select>
                      </FormControl>
                      
                      <TextField
                        fullWidth
                        label="Daily Budget"
                        defaultValue="50.00"
                        InputProps={{
                          startAdornment: '$',
                        }}
                      />
                    </CardContent>
                  </Card>
                </Grid>
              )}
              
              {selectedPlatforms.instagram && (
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <InstagramIcon color="secondary" sx={{ mr: 1 }} />
                        <Typography variant="h6">Instagram Ads</Typography>
                      </Box>
                      
                      <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>Placement</InputLabel>
                        <Select
                          defaultValue="feed"
                          label="Placement"
                        >
                          <MenuItem value="feed">Feed</MenuItem>
                          <MenuItem value="story">Stories</MenuItem>
                          <MenuItem value="explore">Explore</MenuItem>
                          <MenuItem value="reels">Reels</MenuItem>
                        </Select>
                      </FormControl>
                      
                      <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>Call to Action</InputLabel>
                        <Select
                          defaultValue="shop_now"
                          label="Call to Action"
                        >
                          <MenuItem value="shop_now">Shop Now</MenuItem>
                          <MenuItem value="learn_more">Learn More</MenuItem>
                          <MenuItem value="sign_up">Sign Up</MenuItem>
                          <MenuItem value="contact_us">Contact Us</MenuItem>
                        </Select>
                      </FormControl>
                    </CardContent>
                  </Card>
                </Grid>
              )}
              
              {selectedPlatforms.google && (
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <img src="/google-ads-icon.png" alt="Google" style={{ width: 24, height: 24, marginRight: 8 }} />
                        <Typography variant="h6">Google Ads</Typography>
                      </Box>
                      
                      <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>Campaign Type</InputLabel>
                        <Select
                          defaultValue="search"
                          label="Campaign Type"
                        >
                          <MenuItem value="search">Search Campaign</MenuItem>
                          <MenuItem value="display">Display Campaign</MenuItem>
                          <MenuItem value="video">Video Campaign</MenuItem>
                          <MenuItem value="shopping">Shopping Campaign</MenuItem>
                        </Select>
                      </FormControl>
                      
                      <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>Bid Strategy</InputLabel>
                        <Select
                          defaultValue="maximize_clicks"
                          label="Bid Strategy"
                        >
                          <MenuItem value="maximize_clicks">Maximize Clicks</MenuItem>
                          <MenuItem value="target_cpa">Target CPA</MenuItem>
                          <MenuItem value="maximize_conversions">Maximize Conversions</MenuItem>
                          <MenuItem value="target_roas">Target ROAS</MenuItem>
                        </Select>
                      </FormControl>
                      
                      <TextField
                        fullWidth
                        label="Daily Budget"
                        defaultValue="50.00"
                        InputProps={{
                          startAdornment: '$',
                        }}
                      />
                    </CardContent>
                  </Card>
                </Grid>
              )}
              
              {selectedPlatforms.chatbot && (
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <MessageIcon color="success" sx={{ mr: 1 }} />
                        <Typography variant="h6">Reasonic Chatbot</Typography>
                        <Chip label="Exclusive" color="success" size="small" sx={{ ml: 1 }} />
                      </Box>
                      
                      <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>Trigger Method</InputLabel>
                        <Select
                          defaultValue="auto"
                          label="Trigger Method"
                        >
                          <MenuItem value="auto">Auto-popup (after 5s)</MenuItem>
                          <MenuItem value="button">Chat Button</MenuItem>
                          <MenuItem value="scroll">On Scroll (50%)</MenuItem>
                          <MenuItem value="exit">Exit Intent</MenuItem>
                        </Select>
                      </FormControl>
                      
                      <FormGroup>
                        <FormControlLabel
                          control={<Checkbox defaultChecked />}
                          label="Collect visitor email"
                        />
                        <FormControlLabel
                          control={<Checkbox defaultChecked />}
                          label="Track conversion events"
                        />
                        <FormControlLabel
                          control={<Checkbox />}
                          label="Enable lead qualification"
                        />
                      </FormGroup>
                    </CardContent>
                  </Card>
                </Grid>
              )}
              
              {selectedPlatforms.email && (
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <EmailIcon color="action" sx={{ mr: 1 }} />
                        <Typography variant="h6">Email Newsletters</Typography>
                        <Chip label="Exclusive" color="success" size="small" sx={{ ml: 1 }} />
                      </Box>
                      
                      <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>Publisher Category</InputLabel>
                        <Select
                          defaultValue="tech"
                          label="Publisher Category"
                        >
                          <MenuItem value="tech">Technology</MenuItem>
                          <MenuItem value="finance">Finance</MenuItem>
                          <MenuItem value="lifestyle">Lifestyle</MenuItem>
                          <MenuItem value="business">Business</MenuItem>
                        </Select>
                      </FormControl>
                      
                      <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>Placement</InputLabel>
                        <Select
                          defaultValue="main"
                          label="Placement"
                        >
                          <MenuItem value="main">Main Content</MenuItem>
                          <MenuItem value="sidebar">Sidebar</MenuItem>
                          <MenuItem value="sponsored">Sponsored Section</MenuItem>
                        </Select>
                      </FormControl>
                    </CardContent>
                  </Card>
                </Grid>
              )}
            </Grid>
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
              <Button onClick={handleBack}>
                Back
              </Button>
              <Button
                variant="contained"
                onClick={handleNext}
              >
                Continue
              </Button>
            </Box>
          </Box>
        )}
        
        {activeStep === 2 && (
          <Box>
            <Typography variant="h6" gutterBottom>Publish Ads</Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Review your selected platforms and ad variants before publishing. Standard formats will be sent 
              directly to their respective platforms, while exclusive formats will provide embeddable code.
            </Typography>
            
            <Grid container spacing={3}>
              {Object.entries(selectedPlatforms).filter(([_, isSelected]) => isSelected).map(([platform]) => (
                <Grid item xs={12} key={platform}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {platformIcons[platform]}
                        <Typography variant="subtitle1" sx={{ ml: 1 }}>
                          {platform.charAt(0).toUpperCase() + platform.slice(1)}
                        </Typography>
                      </Box>
                      
                      {(platform === 'chatbot' || platform === 'email') ? (
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={copiedStatus[platform === 'chatbot' ? AdFormat.CHATBOT_SCRIPT : AdFormat.EMAIL_NEWSLETTER] ? <CheckCircleIcon /> : <CodeIcon />}
                          onClick={() => handleCopyCode(platform === 'chatbot' ? AdFormat.CHATBOT_SCRIPT : AdFormat.EMAIL_NEWSLETTER)}
                          color={copiedStatus[platform === 'chatbot' ? AdFormat.CHATBOT_SCRIPT : AdFormat.EMAIL_NEWSLETTER] ? "success" : "primary"}
                        >
                          {copiedStatus[platform === 'chatbot' ? AdFormat.CHATBOT_SCRIPT : AdFormat.EMAIL_NEWSLETTER] ? 'Copied!' : 'Copy Code'}
                        </Button>
                      ) : (
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<CloudUploadIcon />}
                          onClick={() => handlePublish(platform)}
                        >
                          Publish
                        </Button>
                      )}
                    </Box>
                    
                    <Divider sx={{ my: 1 }} />
                    
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2">Selected Ad Variants</Typography>
                      
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                        {Object.entries(variantsByFormat).map(([format, variants]) => {
                          // Map the platform to relevant formats
                          const platformFormats: Record<string, string[]> = {
                            facebook: [AdFormat.SOCIAL_MEDIA_POST, AdFormat.DISPLAY_AD],
                            instagram: [AdFormat.SOCIAL_MEDIA_POST],
                            twitter: [AdFormat.SOCIAL_MEDIA_POST],
                            google: [AdFormat.SEARCH_AD, AdFormat.DISPLAY_AD],
                            linkedin: [AdFormat.SOCIAL_MEDIA_POST, AdFormat.DISPLAY_AD],
                            email: [AdFormat.EMAIL_NEWSLETTER],
                            chatbot: [AdFormat.CHATBOT_SCRIPT]
                          };
                          
                          if (platformFormats[platform].includes(format)) {
                            return variants.map((variant, index) => (
                              <Chip
                                key={`${format}-${index}`}
                                label={variant.title || formatName(format)}
                                size="small"
                                sx={{ ml: 0.5 }}
                              />
                            ));
                          }
                          return null;
                        })}
                      </Box>
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
              <Button onClick={handleBack}>
                Back
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleReset}
              >
                Finish
              </Button>
            </Box>
          </Box>
        )}
      </Paper>
      
      <Typography variant="h6" gutterBottom>Exclusive Formats</Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Reasonic's exclusive formats provide high-converting ad experiences that can only be used through 
        our platform. These formats include interactive chatbots and newsletter CTAs with built-in tracking.
      </Typography>
      
      <Grid container spacing={3}>
        {exclusiveFormats.map((format) => {
          if (!variantsByFormat[format]) return null;
          
          return variantsByFormat[format].map((variant, index) => (
            <Grid item xs={12} md={6} key={`${format}-${index}`}>
              <Card variant="outlined">
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {format === AdFormat.CHATBOT_SCRIPT ? <MessageIcon color="success" /> : <EmailIcon color="action" />}
                      <Typography variant="subtitle1" sx={{ ml: 1 }}>
                        {formatName(format)}
                      </Typography>
                      <Chip label="Exclusive" color="success" size="small" sx={{ ml: 1 }} />
                    </Box>
                  </Box>
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {variant.content.slice(0, 100)}...
                  </Typography>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={copiedStatus[format + index] ? <CheckCircleIcon /> : <CodeIcon />}
                      onClick={() => handleCopyCode(format)}
                      color={copiedStatus[format + index] ? "success" : "primary"}
                    >
                      {copiedStatus[format + index] ? 'Copied!' : 'Copy Embed Code'}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ));
        })}
      </Grid>
      
      <Dialog open={showConnectDialog} onClose={handleConnectDialogClose}>
        <DialogTitle>Connect Ad Platform</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Choose a platform to connect with your Reasonic account. Connected platforms allow direct publishing 
            of your generated ad content.
          </DialogContentText>
          
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Platform</InputLabel>
            <Select
              value="facebook"
              label="Platform"
            >
              <MenuItem key="facebook-platform" value="facebook">Facebook Ads</MenuItem>
              <MenuItem key="google-platform" value="google">Google Ads</MenuItem>
              <MenuItem key="twitter-platform" value="twitter">Twitter Ads</MenuItem>
              <MenuItem key="linkedin-platform" value="linkedin">LinkedIn Ads</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleConnectDialogClose}>Cancel</Button>
          <Button variant="contained" onClick={handleConnectDialogClose}>
            Connect
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DistributionTab; 